/**
 * 店舗タブの KPI カード・グラフ用の変換（純粋関数）。ホームと同じ定義（adapters/kurocoData）を使い、
 * 入力だけを店舗に差し替える。
 *
 *   カード: 今月の値 = store-summary の kpis。比較は「先月同時期」（prev_month_same_period）。
 *           先月同時期を日次から出せない項目（離脱率・カルテ枚数など）は、比較が「—」になる。
 *   グラフ: 実績 = /kpis/history（店舗単位）、予測 = /forecasts?store=X の店舗合計の行、
 *           目標線 = 売上だけ（店舗の目標合計）。
 */
import { buildKurocoHomeData, buildScorecard, SC_TO_METRIC } from '../adapters/kurocoData';
import { EM } from '../components/kuroco/fmt';

/** 先月同時期（日次ビューのキー）→ /kpis と同じキー。同名のキーはそのまま引き継ぐ。 */
const DAY_KEY_TO_KPI_KEY = {
  total_customers: 'customer_count',
  new_customers: 'new_customer_count',
  repeat_customers: 'repeat_customer_count',
};

/** 'YYYY-MM' の 1 つ前の月。形式が違えば null。 */
export function previousMonthKey(month) {
  const m = /^(\d{4})-(\d{2})$/.exec(month || '');
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 先月同時期の KPI を、/kpis と同じキー名の 1 行にする。無ければ null。 */
export function prevPeriodAsKpis(prev) {
  if (!prev || typeof prev !== 'object') return null;
  const out = {};
  Object.entries(prev).forEach(([key, value]) => {
    if (key === 'through') return;
    out[DAY_KEY_TO_KPI_KEY[key] || key] = value;
  });
  return out;
}

/**
 * KPI カード（ホームと同じ項目・同じ並び）。比較は先月同時期（prev に出る）。前年は出さない（year は「—」）。
 * entry = store-summary の 1 店舗分。
 */
export function storeKpiCards(entry, selectedMonth) {
  const prevKey = previousMonthKey(selectedMonth);
  const yearKey = prevKey ? `${Number(selectedMonth.slice(0, 4)) - 1}${selectedMonth.slice(4)}` : null;
  // 比べる月の行は、空でも必ず渡す。行が無いと、ホームの変換は今月の値と比べてしまい「▲ +0.0%」になる
  const history = [];
  if (prevKey) history.push({ month: prevKey, ...(prevPeriodAsKpis(entry?.prev_month_same_period) || {}) });
  if (yearKey) history.push({ month: yearKey });
  // 比べる値が無いとき、ホームの変換は prev / year を空（undefined）で返す項目がある。「—」に揃える
  return buildScorecard(entry?.kpis || null, history, selectedMonth).map((c) => ({
    ...c,
    prev: c.prev || EM,
    year: c.year || EM,
    toneP: c.toneP || 'flat',
    toneY: c.toneY || 'flat',
  }));
}

/** カード → グラフの指標キー（グラフに無い項目は null。タップしても何も起きない）。 */
export function metricKeyOfCard(card) {
  return SC_TO_METRIC[card?.key] || null;
}

/** 店舗合計の予測の行だけ（個人の行は forecast_people が無いので除く）。月ごとに 1 行。 */
export function storeForecastRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter((r) => r && r.forecast_people != null);
}

/** 店舗の目標線の入力。売上だけ（目標合計が無ければ null = 目標線なし）。 */
export function storeGoals(entry) {
  const total = Number(entry?.goal_total);
  return Number.isFinite(total) && total > 0 ? { finalSales: total } : null;
}

/**
 * グラフ用データ（metrics / metricOrder / chartMonths）。ホームと同じ buildKurocoHomeData を通す。
 * history = /kpis/history の history、forecasts = storeForecastRows の結果。
 */
export function storeChartData({ entry, history, forecasts, selectedMonth }) {
  return buildKurocoHomeData({
    kpis: entry?.kpis || null,
    kpiHistory: Array.isArray(history) ? history : [],
    mlForecasts: forecasts || [],
    aiComment: null,
    loading: {},
    selectedMonth,
    stylistName: entry?.store || '',
    goals: storeGoals(entry),
  });
}
